// A board's grid (TOM-192, boards-v3.md "Item sizes and layout"), computed
// here and consumed by CSS as a span on each item, so the layout is tested
// rather than left to the browser (ux_principles.md, layout truth rules).
//
// A board lays out on 4 columns when it is at least 720 px wide, and on 2
// otherwise: the board's own width decides, so it narrows when a panel opens
// beside it. On 4 columns `l` spans 4, `m` 2 and `s` 1; on 2 columns `l` and
// `m` span 2 and `s` 1. No row ends in a hole: when the next item does not fit,
// the row's last item takes the space left, and the last row fills the same way.
//
// Items keep their own height and are packed in list order (TOM-685, cloud ADR
// 0398): a run of items too long for one row goes into two half-width lanes on
// 4 columns, rather than rows as tall as their tallest item. `boardShape` gives
// the spans, from sizes and the column count alone; `packBoard` places the
// items from their measured heights.
//
// A diagram is laid out at least medium (TOM-655): a small item is too narrow
// for its text at the smallest legible scale. Like a row's widening, this is
// layout only: the size stored stays the owner's, and the editing state says
// why the item is wider than its size.

import {
  neoBoardItemModule,
  neoBoardItemSourceSpec,
  type NeoBoardAnyItem,
} from '@neomaris/protocol';

export type BoardItemSize = 's' | 'm' | 'l';

/** The narrowest board drawn on 4 columns. */
export const BOARD_WIDE_MIN_PX = 720;

export type BoardColumns = 2 | 4;

export function boardColumns(widthPx: number): BoardColumns {
  return widthPx >= BOARD_WIDE_MIN_PX ? 4 : 2;
}

function baseSpan(size: BoardItemSize, columns: BoardColumns): number {
  if (columns === 4) return size === 'l' ? 4 : size === 'm' ? 2 : 1;
  return size === 's' ? 1 : 2;
}

/** Each item's span, in order. */
export function boardItemSpans(sizes: readonly BoardItemSize[], columns: BoardColumns): number[] {
  const spans = sizes.map((size) => baseSpan(size, columns));
  let used = 0;
  let rowLast = -1;
  spans.forEach((span, i) => {
    if (used + span > columns) {
      // The row is full: its last item takes what is left.
      if (rowLast >= 0) spans[rowLast]! += columns - used;
      used = 0;
    }
    used += span;
    rowLast = i;
  });
  if (rowLast >= 0 && used < columns) spans[rowLast]! += columns - used;
  return spans;
}

/**
 * The size an item of `kind` is laid out at: its own, except a small diagram,
 * laid out as medium (TOM-655). Its spans come from these sizes.
 */
export function boardItemDrawnSize(size: BoardItemSize, kind: string): BoardItemSize {
  // A page holding the anchor watch is never drawn small: its live area needs
  // 320 × 240 (cloud ADR 0393 §5.1, TOM-665).
  return (kind === 'diagram' || kind === 'anchor-watch') && size === 's' ? 'm' : size;
}

/**
 * The kind an item is laid out as (TOM-665): a template page as the item it
 * stands for (`neoBoardItemSourceSpec`, so a small diagram page draws medium as
 * a small diagram does), and any item holding the anchor-watch module as
 * `anchor-watch`.
 */
export function boardItemSizeKind(item: Pick<NeoBoardAnyItem, 'spec'>): string {
  const module = neoBoardItemModule(item);
  if (module?.module === 'anchor-watch') return 'anchor-watch';
  return neoBoardItemSourceSpec(item).kind;
}

/**
 * Which items are drawn wider than their own size, to fill their row. The
 * editing state says so beside the size control, which still shows the size
 * stored, so an S that draws as wide as an M is not a size that failed to save.
 */
export function boardItemsWidened(
  sizes: readonly BoardItemSize[],
  spans: readonly number[],
  columns: BoardColumns
): boolean[] {
  return sizes.map((size, i) => (spans[i] ?? 0) > baseSpan(size, columns));
}

/**
 * Why an item is drawn wider than its size: its kind's least size, its row,
 * or its lane (a small item with no small item beside it, TOM-685).
 */
export type BoardItemWidening = 'kind' | 'row' | 'lane';

/**
 * For each item, why it is drawn wider than the size stored, or null when it
 * is not: `kind` when its kind is laid out larger (a small diagram), `lane`
 * when it is a lone small item in a lane (`laned`, from `boardShape`), `row`
 * when it fills its row.
 */
export function boardItemsWidening(
  sizes: readonly BoardItemSize[],
  drawnSizes: readonly BoardItemSize[],
  spans: readonly number[],
  columns: BoardColumns,
  laned: readonly boolean[] = []
): Array<BoardItemWidening | null> {
  const widened = boardItemsWidened(sizes, spans, columns);
  return sizes.map((size, i) => {
    if (!widened[i]) return null;
    return drawnSizes[i] !== size ? 'kind' : laned[i] === true ? 'lane' : 'row';
  });
}

/**
 * A stretch of the board, in list order (TOM-685): a `row` of items sharing a
 * top, or a run in two half-width `lanes`. A lane takes blocks: an `m`, two `s`
 * next to each other side by side, or a lone `s` drawn half width. Items are
 * list indexes.
 */
export type BoardSegment =
  | { kind: 'row'; items: number[]; spans: number[] }
  | { kind: 'lanes'; blocks: number[][] };

export interface BoardShape {
  segments: BoardSegment[];
  /** Each item's span, in order. */
  spans: number[];
  /** Whether each item is in a run laid out in lanes. */
  laned: boolean[];
}

/** Whether an item takes the board's whole width on `columns`: `l`, and `m` on 2. */
function fullWidth(size: BoardItemSize, columns: BoardColumns): boolean {
  return baseSpan(size, columns) === columns;
}

/**
 * How the items share the board's width (TOM-685, cloud ADR 0398), from their
 * drawn sizes and the column count alone; heights never change it.
 *
 * The list splits into runs at full-width items, each of which is a row of its
 * own. A run that fits in one row is that row, laid out as `boardItemSpans`
 * lays it out, its last item widened to fill it. A longer run on 4 columns goes
 * into two lanes. On 2 columns every run is rows, so the spans are exactly
 * `boardItemSpans`' (the iPhone draws the same).
 */
export function boardShape(drawn: readonly BoardItemSize[], columns: BoardColumns): BoardShape {
  const segments: BoardSegment[] = [];
  const rows = (items: number[]) => {
    const spans = boardItemSpans(
      items.map((i) => drawn[i]!),
      columns
    );
    // boardItemSpans fills every row, so a row ends where its spans make the width.
    let row: BoardSegment & { kind: 'row' } = { kind: 'row', items: [], spans: [] };
    let used = 0;
    items.forEach((i, n) => {
      row.items.push(i);
      row.spans.push(spans[n]!);
      used += spans[n]!;
      if (used >= columns) {
        segments.push(row);
        row = { kind: 'row', items: [], spans: [] };
        used = 0;
      }
    });
  };
  let run: number[] = [];
  const endRun = () => {
    const width = run.reduce((sum, i) => sum + baseSpan(drawn[i]!, columns), 0);
    if (columns === 2 || width <= columns) rows(run);
    else segments.push({ kind: 'lanes', blocks: laneBlocks(run, drawn) });
    run = [];
  };
  drawn.forEach((size, i) => {
    if (!fullWidth(size, columns)) {
      run.push(i);
      return;
    }
    endRun();
    rows([i]);
  });
  endRun();

  const spans: number[] = [];
  const laned: boolean[] = [];
  for (const segment of segments) {
    if (segment.kind === 'row') {
      segment.items.forEach((i, n) => {
        spans[i] = segment.spans[n]!;
        laned[i] = false;
      });
      continue;
    }
    for (const block of segment.blocks) {
      for (const i of block) {
        spans[i] = block.length === 2 ? 1 : 2;
        laned[i] = true;
      }
    }
  }
  return { segments, spans, laned };
}

/** A lane run's blocks: an `m`, two `s` next to each other, or a lone `s`. */
function laneBlocks(run: readonly number[], drawn: readonly BoardItemSize[]): number[][] {
  const blocks: number[][] = [];
  for (let n = 0; n < run.length; n++) {
    const i = run[n]!;
    const next = run[n + 1];
    if (drawn[i] === 's' && next !== undefined && drawn[next] === 's') {
      blocks.push([i, next]);
      n++;
    } else blocks.push([i]);
  }
  return blocks;
}

/**
 * A packed board's rows are this tall, px. An item spans as many as its own
 * height and the gap under it need, so items keep their height (TOM-685).
 */
export const BOARD_ROW_PX = 4;

/**
 * Lanes whose ends are this close, px, end level: the next blocks start side
 * by side rather than a few px apart, which reads as a misaligned row. The
 * shorter lane's last gap grows by as much.
 */
export const BOARD_LANE_SNAP_PX = 24;

/** The rows an item `heightPx` tall spans, with the `gapPx` under it. */
export function boardItemRows(heightPx: number, gapPx: number): number {
  return Math.max(1, Math.ceil((Math.max(0, heightPx) + gapPx) / BOARD_ROW_PX));
}

/** Where an item sits on the packed grid: grid lines from 1, as CSS takes them. */
export interface BoardPlace {
  col: number;
  span: number;
  /** In rows of BOARD_ROW_PX. */
  row: number;
  rows: number;
  /** The lane, 0 left or 1 right, of an item in a lane run; null in a row. */
  lane: 0 | 1 | null;
}

/** A lane: 0 left, 1 right. */
export type BoardLane = 0 | 1;

/**
 * Each item's place on the board (TOM-685, cloud ADR 0398), in list order, from
 * its drawn size, the column count and its measured height.
 *
 * A row's items share a top under everything before them. In a lane run each
 * block goes to the lane where it starts highest, ties to the left, unless
 * `pins` (by the block's first item) holds it to a lane that starts no more
 * than `slackPx` lower: a pin never holds a tile over a hole bigger than that.
 * Lanes that end within BOARD_LANE_SNAP_PX of each other end level. Either
 * way a block never starts above the block before it, and on its left starts
 * at least BOARD_LANE_SNAP_PX lower, so reading the board top to bottom, then
 * left to right, is the list's order: Neo's
 * positions, "first" and Move earlier/later keep their meaning. A full-width
 * item starts under both lanes.
 */
export function packBoard(
  drawn: readonly BoardItemSize[],
  columns: BoardColumns,
  heights: readonly number[],
  gapPx: number,
  pins: ReadonlyMap<number, BoardLane> = new Map(),
  slackPx = Infinity
): BoardPlace[] {
  const rowsOf = (i: number) => boardItemRows(heights[i] ?? 0, gapPx);
  const places: BoardPlace[] = [];
  const half = columns / 2;
  let cursor = 1;
  for (const segment of boardShape(drawn, columns).segments) {
    if (segment.kind === 'row') {
      let col = 1;
      let bottom = cursor + 1;
      segment.items.forEach((i, n) => {
        const span = segment.spans[n]!;
        places[i] = { col, span, row: cursor, rows: rowsOf(i), lane: null };
        col += span;
        bottom = Math.max(bottom, cursor + rowsOf(i));
      });
      cursor = bottom;
      continue;
    }
    const top: [number, number] = [cursor, cursor];
    let floor = cursor;
    let before: BoardLane | null = null;
    const snapRows = Math.ceil(BOARD_LANE_SNAP_PX / BOARD_ROW_PX);
    const startIn = (lane: BoardLane) => {
      const row = Math.max(top[lane], floor);
      // Level with the block before, a block must sit to its right. On its
      // left it starts a clear step lower, never a few px, which reads as a
      // row out of line.
      return before !== null && lane < before && row < floor + snapRows ? floor + snapRows : row;
    };
    for (const block of segment.blocks) {
      if (Math.abs(top[0] - top[1]) * BOARD_ROW_PX <= BOARD_LANE_SNAP_PX) {
        top[0] = top[1] = Math.max(top[0], top[1]);
      }
      const best: BoardLane = startIn(1) < startIn(0) ? 1 : 0;
      const pinned = pins.get(block[0]!);
      const lane =
        pinned !== undefined && (startIn(pinned) - startIn(best)) * BOARD_ROW_PX <= slackPx
          ? pinned
          : best;
      const row = startIn(lane);
      const span = block.length === 2 ? half / 2 : half;
      block.forEach((i, n) => {
        places[i] = { col: lane * half + n * span + 1, span, row, rows: rowsOf(i), lane };
      });
      top[lane] = row + Math.max(...block.map(rowsOf));
      floor = row;
      before = lane;
    }
    cursor = Math.max(...top);
  }
  return places;
}
